import { BaseDateChartData } from "../../../../../../../components/charts/BaseDateDataChart/types";
import { InstallDetection } from "../../../../../../../apis/companySearch/marketPenetrationOverTime";
import { CHART_CONSTANTS } from "../../../../constants";

/**
 * Calculates the start date of the last completed quarter
 * @returns A date string in YYYY-MM-DD format
 */
const getLastCompletedQuarterDate = (): string => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const quarterStartMonth =
    currentMonth <= 3 ? 10 : currentMonth <= 6 ? 1 : currentMonth <= 9 ? 4 : 7;

  const quarterYear = currentMonth <= 3 ? currentYear - 1 : currentYear;

  return `${quarterYear}-${String(quarterStartMonth).padStart(2, "0")}-01`;
};

/**
 * Transforms monthly data into quarterly aggregated data
 * @param monthlyData Array of installation data by month
 * @returns Array of installation data aggregated by quarter
 */
const aggregateDataByQuarter = (
  monthlyData: InstallDetection[]
): InstallDetection[] => {
  const lastCompletedQuarter = getLastCompletedQuarterDate();

  const quarterlyAggregations: Record<
    string,
    { productId: number; companyCount: number; marketCount: number }
  > = {};

  for (const {
    productId,
    monthStartDate,
    companyCount,
    marketCount,
  } of monthlyData) {
    const [year, month] = monthStartDate.split("-").map(Number);

    const quarterStartMonth =
      month <= 3 ? "01" : month <= 6 ? "04" : month <= 9 ? "07" : "10";

    const quarterKey = `${year}-${quarterStartMonth}-01`;

    if (quarterKey > lastCompletedQuarter) continue;

    if (!quarterlyAggregations[quarterKey]) {
      quarterlyAggregations[quarterKey] = {
        productId,
        companyCount: 0,
        marketCount: 0,
      };
    }

    quarterlyAggregations[quarterKey].companyCount += companyCount;
    quarterlyAggregations[quarterKey].marketCount += marketCount;
  }

  return Object.entries(quarterlyAggregations).map(
    ([quarterDate, { productId, companyCount, marketCount }]) => ({
      productId,
      monthStartDate: quarterDate,
      companyCount,
      marketCount,
    })
  );
};

/**
 * Groups installation data by product ID
 * @param installData Array of installation detections
 * @returns Map of installation data grouped by product ID
 */
const groupInstallationsByProduct = (
  installData: InstallDetection[]
): Record<number, InstallDetection[]> => {
  const groupedData: Record<number, InstallDetection[]> = {};

  for (const installation of installData) {
    const { productId } = installation;

    if (!groupedData[productId]) {
      groupedData[productId] = [];
    }

    groupedData[productId].push(installation);
  }

  return groupedData;
};

/**
 * Calculates the average installation count for each product
 * @param productInstallations Map of installation data grouped by product ID
 * @returns Map of average installation counts by product ID
 */
const calculateAverageInstallsByProduct = (
  productInstallations: Record<number, InstallDetection[]>
): Record<number, number> => {
  const averages: Record<number, number> = {};

  for (const [productId, installations] of Object.entries(
    productInstallations
  )) {
    let totalInstalls = 0;

    for (const { companyCount } of installations) {
      totalInstalls += companyCount;
    }

    averages[Number(productId)] = Math.floor(
      totalInstalls / installations.length
    );
  }

  return averages;
};

/**
 * Identifies the top products based on average installation count
 * @param averageInstalls Map of average installation counts by product ID
 * @param limit Number of top products to return
 * @returns Array of [productId, averageInstalls] pairs for top products
 */
const findTopProductsByAverageInstalls = (
  averageInstalls: Record<number, number>,
  limit = CHART_CONSTANTS.competitorMarketPenetrationTrend.topProducts
): [string, number][] => {
  return Object.entries(averageInstalls)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, limit);
};

/**
 * Converts installation data to cumulative values over time
 * @param installations Array of installation data
 * @returns Array of installation data with cumulative company counts
 */
const convertToCumulativeValues = (
  installations: InstallDetection[]
): InstallDetection[] => {
  let cumulativeCount = 0;

  return installations.map((installation) => {
    cumulativeCount += installation.companyCount;

    return {
      ...installation,
      companyCount: cumulativeCount,
    };
  });
};

/**
 * Groups installation data by date
 * @param installations Array of installation data
 * @returns Map of installation data grouped by date
 */
const groupInstallationsByDate = (
  installations: InstallDetection[]
): Record<string, InstallDetection[]> => {
  const groupedByDate: Record<string, InstallDetection[]> = {};

  for (const installation of installations) {
    const { monthStartDate } = installation;

    if (!groupedByDate[monthStartDate]) {
      groupedByDate[monthStartDate] = [];
    }

    groupedByDate[monthStartDate].push(installation);
  }

  return groupedByDate;
};

/**
 * Transforms installation data to BaseDateChartData
 * @param dateGroupedInstallations Map of installation data grouped by date
 * @param productNameMap Map of product names by product ID
 * @param marketSize Total market size for percentage calculations
 * @returns Array of chart data points
 */
const transformToChartData = (
  dateGroupedInstallations: Record<string, InstallDetection[]>,
  productNameMap: Record<number, string>,
  marketSize: number
): BaseDateChartData[] => {
  return Object.entries(dateGroupedInstallations)
    .map(([date, installations]) => ({
      date,
      series: installations
        .filter(
          ({ companyCount }) =>
            companyCount > 0 && Number.isFinite(companyCount)
        )
        .map(({ companyCount, productId }) => {
          const percentage =
            marketSize > 0 ? (companyCount / marketSize) * 100 : 0;
          const validPercentage =
            Number.isFinite(percentage) && percentage >= 0 ? percentage : 0;
          return {
            name: productNameMap[productId] || "Unknown",
            value: Math.floor(validPercentage),
            companyCount,
          };
        })
        .filter(({ value }) => value > 0), // Filter out zero percentages
    }))
    .filter(({ series }) => series.length > 0); // Filter out dates with no valid data
};

/**
 * Main function to generate chart data for competitor market penetration trends
 * @param installDetections Array of InstallDetection
 * @param productsNameMapById Map of product names by product ID
 * @param marketSize Total market size for percentage calculations
 * @returns BaseDateChartData[]
 */
export const getBaseDateChartData = (
  installDetections: InstallDetection[],
  productsNameMapById: Record<number, string>,
  marketSize: number
): BaseDateChartData[] => {
  const installationsByProduct = groupInstallationsByProduct(installDetections);
  const averageInstallsByProduct = calculateAverageInstallsByProduct(
    installationsByProduct
  );
  const topProducts = findTopProductsByAverageInstalls(
    averageInstallsByProduct
  );

  const topProductIds = new Set(topProducts.map(([id]) => Number(id)));
  const topProductInstallations: InstallDetection[] = [];

  for (const [productId, installations] of Object.entries(
    installationsByProduct
  )) {
    if (topProductIds.has(Number(productId))) {
      const quarterlyData = aggregateDataByQuarter(installations);
      const cumulativeData = convertToCumulativeValues(quarterlyData);

      for (const installation of cumulativeData) {
        topProductInstallations.push(installation);
      }
    }
  }

  const installationsByDate = groupInstallationsByDate(topProductInstallations);
  return transformToChartData(
    installationsByDate,
    productsNameMapById,
    marketSize
  );
};
